import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Input,
  Popconfirm,
  Row,
  Segmented,
  Space,
  Statistic,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import { FilterSelect, filterActiveClassName } from '@components/common/FilterSelect';
import { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { SorterResult } from 'antd/es/table/interface';
import { SortOrder } from 'antd/es/table/interface';
import dayjs from 'dayjs';
import { Link, useSearchParams } from 'react-router-dom';
import * as api from '@apis/group-monitoring';
import ReservationSuccessPageHeader from '@components/reservation-success/ReservationSuccessPageHeader';
import GroupMonitoringDetailDrawer from '@components/group-monitoring/GroupMonitoringDetailDrawer';

function downloadAuditJson(data: GroupMonitoringAuditResponse, dateType: 'upcoming' | 'past') {
  const now = dayjs().format('YYYYMMDD_HHmmss');
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json;charset=utf-8' });
  const fileName = `group-monitoring-audit-${dateType}-${now}.json`;
  const nav = window.navigator as Navigator & { msSaveOrOpenBlob?: (b: Blob, name: string) => void };
  if (nav.msSaveOrOpenBlob) {
    nav.msSaveOrOpenBlob(blob, fileName);
    return;
  }
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
import EnrollmentSourceDrawer from '@components/grouping-agent/EnrollmentSourceDrawer';
import {
  GroupMonitoringAuditIssue,
  GroupMonitoringAuditResponse,
  GroupMonitoringGroup,
  GroupMonitoringOptionRow,
  GroupMonitoringPartnerRow,
  GroupMonitoringProductRow,
  GroupMonitoringSortField,
  GroupMonitoringUnassignedEnrollment,
  ReprocessAssignmentGapResponse,
} from '@models/group-monitoring';
import {
  buildGroupMonitoringColumns,
  formatShortGroupCountStat,
  shortGroupCountColumn,
  withListServerSort,
} from '@utils/group-monitoring-table';

const { Text } = Typography;

const dateTypeOptions = [
  { value: 'upcoming' as const, label: '예정 회차' },
  { value: 'past' as const, label: '지난 회차' },
];

const summaryTitleByDateType: Record<'upcoming' | 'past', string> = {
  upcoming: '예정 회차 요약',
  past: '지난 회차 요약',
};

const tablePageSizeOptions = [50, 100];

const tablePaginationConfig = (pageSize: number, onPageSizeChange: (size: number) => void) => ({
  pageSize,
  showSizeChanger: true,
  pageSizeOptions: tablePageSizeOptions,
  onShowSizeChange: (_current: number, size: number) => onPageSizeChange(size),
});

const optionReprocessKey = (r: Pick<GroupMonitoringUnassignedEnrollment, 'productId' | 'optionId' | 'startDate'>) =>
  `${r.productId}|${r.optionId || ''}|${r.startDate}`;

const GROUP_MONITORING_TABS = ['unassigned', 'groups', 'options', 'partners', 'products'];

const GroupMonitoringPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab');
  const initialGroupStatus = searchParams.get('groupStatus');
  const [dateType, setDateType] = useState<'upcoming' | 'past'>('upcoming');
  const [reprocessingOptionKey, setReprocessingOptionKey] = useState<string | null>(null);
  const [partnerId, setPartnerId] = useState<string | undefined>();
  const [productId, setProductId] = useState<string | undefined>();
  const [groupStatus, setGroupStatus] = useState<'all' | 'complete' | 'short'>(
    initialGroupStatus === 'complete' || initialGroupStatus === 'short' ? initialGroupStatus : 'all',
  );
  const [keyword, setKeyword] = useState(searchParams.get('keyword') || '');
  const [kakaoOnly, setKakaoOnly] = useState(false);
  const [wideAgeGapOnly, setWideAgeGapOnly] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [groupSortField, setGroupSortField] = useState<GroupMonitoringSortField | undefined>();
  const [groupSortOrder, setGroupSortOrder] = useState<SortOrder | undefined>();
  const [unassignedPage, setUnassignedPage] = useState(0);
  const [unassignedPageSize, setUnassignedPageSize] = useState(50);
  const [optionsPage, setOptionsPage] = useState(0);
  const [optionsPageSize, setOptionsPageSize] = useState(50);
  const [partnersPage, setPartnersPage] = useState(0);
  const [partnersPageSize, setPartnersPageSize] = useState(50);
  const [productsPage, setProductsPage] = useState(0);
  const [productsPageSize, setProductsPageSize] = useState(50);
  const [listSortField, setListSortField] = useState<GroupMonitoringSortField | undefined>();
  const [listSortOrder, setListSortOrder] = useState<SortOrder | undefined>();
  const [activeTab, setActiveTab] = useState(
    initialTab && GROUP_MONITORING_TABS.includes(initialTab) ? initialTab : 'unassigned',
  );
  const [detailGroupId, setDetailGroupId] = useState<string | null>(null);
  const [sourceScheduleId, setSourceScheduleId] = useState<string | null>(null);
  const [auditIssuesCodeFilter, setAuditIssuesCodeFilter] = useState<string | undefined>();
  const [auditOnlyWideAgeGap, setAuditOnlyWideAgeGap] = useState(false);
  const [auditData, setAuditData] = useState<Awaited<ReturnType<typeof api.getGroupMonitoringAudit>>['data'] | null>(
    null,
  );

  const listQuery = useMemo(
    () => ({ dateType, partnerId, productId, keyword: keyword.trim() || undefined }),
    [dateType, partnerId, productId, keyword],
  );

  const listSortParams = useMemo(
    () => ({
      sortField: listSortField,
      sortOrder:
        listSortOrder === 'ascend' ? ('asc' as const) : listSortOrder === 'descend' ? ('desc' as const) : undefined,
    }),
    [listSortField, listSortOrder],
  );

  const queryParams = useMemo(
    () => ({
      dateType,
      partnerId,
      productId,
      keyword: keyword.trim() || undefined,
      groupStatus: groupStatus === 'all' ? undefined : groupStatus,
      hasKakaoEnrollment: kakaoOnly ? true : undefined,
      hasWideAgeGap: wideAgeGapOnly ? true : undefined,
      page,
      size: pageSize,
      sortField: groupSortField,
      sortOrder:
        groupSortOrder === 'ascend' ? ('asc' as const) : groupSortOrder === 'descend' ? ('desc' as const) : undefined,
    }),
    [
      dateType,
      partnerId,
      productId,
      keyword,
      groupStatus,
      kakaoOnly,
      wideAgeGapOnly,
      page,
      pageSize,
      groupSortField,
      groupSortOrder,
    ],
  );

  const summaryQuery = useQuery({
    queryKey: ['group-monitoring-summary', dateType, partnerId, productId],
    queryFn: () => api.getGroupMonitoringSummary({ dateType, partnerId, productId }),
  });

  useEffect(() => {
    setAuditData(null);
    setAuditIssuesCodeFilter(undefined);
    setAuditOnlyWideAgeGap(false);
  }, [dateType, partnerId, productId]);

  const partnersQuery = useQuery({
    queryKey: ['group-monitoring-partners', dateType, partnerId, partnersPage, partnersPageSize, listSortParams],
    queryFn: () =>
      api.getGroupMonitoringPartners({
        dateType,
        partnerId,
        page: partnersPage,
        size: partnersPageSize,
        ...listSortParams,
      }),
  });

  const productsQuery = useQuery({
    queryKey: [
      'group-monitoring-products',
      dateType,
      partnerId,
      productId,
      productsPage,
      productsPageSize,
      listSortParams,
    ],
    queryFn: () =>
      api.getGroupMonitoringProducts({
        dateType,
        partnerId,
        productId,
        page: productsPage,
        size: productsPageSize,
        ...listSortParams,
      }),
  });

  const optionsQuery = useQuery({
    queryKey: [
      'group-monitoring-options',
      dateType,
      partnerId,
      productId,
      optionsPage,
      optionsPageSize,
      listSortParams,
    ],
    queryFn: () =>
      api.getGroupMonitoringOptions({
        dateType,
        partnerId,
        productId,
        page: optionsPage,
        size: optionsPageSize,
        ...listSortParams,
      }),
  });

  const groupsQuery = useQuery({
    queryKey: ['group-monitoring-groups', queryParams],
    queryFn: () => api.getGroupMonitoringGroups(queryParams),
  });

  const unassignedQuery = useQuery({
    queryKey: ['group-monitoring-unassigned', listQuery, unassignedPage, unassignedPageSize],
    queryFn: () =>
      api.getGroupMonitoringUnassignedEnrollments({
        ...listQuery,
        page: unassignedPage,
        size: unassignedPageSize,
      }),
  });

  const summary = summaryQuery.data?.data;
  const unassigned = unassignedQuery.data?.data;
  const groups = groupsQuery.data?.data;
  const partners = partnersQuery.data?.data;
  const products = productsQuery.data?.data;
  const options = optionsQuery.data?.data;
  const listServerSort = useMemo(
    () => ({ field: listSortField, order: listSortOrder }),
    [listSortField, listSortOrder],
  );

  const reprocessGapMutation = useMutation({
    mutationFn: api.reprocessGroupMonitoringAssignmentGap,
    onSuccess: (res) => {
      const body = res.data as ReprocessAssignmentGapResponse;
      if (body.result === 'OK') {
        const after = body.unassignedCountAfter ?? 0;
        message.success(
          body.message ||
            `회차 재배정 완료 (갭 ${body.unassignedCountBefore ?? '?'} → ${after}건, 그룹 ${body.groupCount ?? '-'}개)`,
        );
      } else if (body.result === 'SKIPPED') {
        message.warning(body.message || '재배정이 스킵되었습니다.');
      } else {
        message.error(body.message || '재배정에 실패했습니다.');
      }
      queryClient.invalidateQueries({ queryKey: ['group-monitoring-summary'] });
      queryClient.invalidateQueries({ queryKey: ['group-monitoring-unassigned'] });
      queryClient.invalidateQueries({ queryKey: ['group-monitoring-groups'] });
      queryClient.invalidateQueries({ queryKey: ['group-monitoring-options'] });
      queryClient.invalidateQueries({ queryKey: ['group-monitoring-partners'] });
      queryClient.invalidateQueries({ queryKey: ['group-monitoring-products'] });
    },
    onError: (err: { response?: { data?: { message?: string | string[] } } }) => {
      const msg = err.response?.data?.message;
      message.error(Array.isArray(msg) ? msg.join(', ') : msg || '회차 재배정 요청 실패');
    },
    onSettled: () => setReprocessingOptionKey(null),
  });

  const invalidateGroupMonitoringQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['group-monitoring-summary'] });
    queryClient.invalidateQueries({ queryKey: ['group-monitoring-groups'] });
    queryClient.invalidateQueries({ queryKey: ['group-monitoring-options'] });
    queryClient.invalidateQueries({ queryKey: ['group-monitoring-partners'] });
    queryClient.invalidateQueries({ queryKey: ['group-monitoring-products'] });
    queryClient.invalidateQueries({ queryKey: ['group-monitoring-unassigned'] });
    setAuditData(null);
  };

  const pruneMutation = useMutation({
    mutationFn: () => api.pruneStaleGroupMembers({ dateType, partnerId, productId }),
    onSuccess: (res) => {
      const body = res.data;
      message.success(
        `환불/취소 참여자 제거: ${body.removedScheduleIds}건 (${body.optionsChanged}/${body.optionCount}회차, 모니터링 그룹 ${body.monitoringGroupsUpdated}개 갱신)`,
      );
      invalidateGroupMonitoringQueries();
    },
    onError: (err: { response?: { data?: { message?: string | string[] } } }) => {
      const msg = err.response?.data?.message;
      message.error(Array.isArray(msg) ? msg.join(', ') : msg || '환불/취소 참여자 제거 실패');
    },
  });

  const deleteEmptyGroupsMutation = useMutation({
    mutationFn: () =>
      api.deleteEmptyGroups({
        dateType,
        partnerId,
        productId,
        keyword: keyword.trim() || undefined,
        groupStatus: groupStatus === 'all' ? undefined : groupStatus,
        hasKakaoEnrollment: kakaoOnly ? true : undefined,
      }),
    onSuccess: (res) => {
      const body = res.data;
      if (body.deletedCount > 0) {
        message.success(`0명 그룹 ${body.deletedCount}개 삭제 (조회 ${body.scannedCount}개 중)`);
      } else {
        message.info('삭제할 0명 그룹이 없습니다.');
      }
      setPage(0);
      invalidateGroupMonitoringQueries();
    },
    onError: (err: { response?: { data?: { message?: string | string[] } } }) => {
      const msg = err.response?.data?.message;
      message.error(Array.isArray(msg) ? msg.join(', ') : msg || '0명 그룹 삭제 실패');
    },
  });

  const recomputeAgeGapsMutation = useMutation({
    mutationFn: () =>
      api.recomputeAgeGaps({
        dateType,
        partnerId,
        productId,
        keyword: keyword.trim() || undefined,
        groupStatus: groupStatus === 'all' ? undefined : groupStatus,
        hasKakaoEnrollment: kakaoOnly ? true : undefined,
        hasWideAgeGap: wideAgeGapOnly ? true : undefined,
        ageGapThreshold: 3,
      }),
    onSuccess: (res) => {
      message.success(`나이 편차 재계산 완료: ${res.data.updatedCount}개 (조회 ${res.data.scannedCount}개)`);
      invalidateGroupMonitoringQueries();
    },
    onError: (err: { response?: { data?: { message?: string | string[] } } }) => {
      const msg = err.response?.data?.message;
      message.error(Array.isArray(msg) ? msg.join(', ') : msg || '나이 편차 재계산 실패');
    },
  });

  const auditMutation = useMutation({
    mutationFn: () =>
      api.getGroupMonitoringAudit({
        dateType,
        partnerId,
        productId,
        includeSchedules: true,
        ageGapThreshold: 3,
        issueSampleLimit: 20,
      }),
    onSuccess: (res) => {
      setAuditData(res.data);
      downloadAuditJson(res.data, dateType);
      const issueCount = res.data.issues.length;
      message.success(
        issueCount === 0
          ? '검증 완료: 이슈 없음 · RAW+검증 JSON 다운로드됨'
          : `검증 완료: 이슈 ${issueCount}건 · RAW+검증 JSON 다운로드됨`,
      );
    },
    onError: (err: { response?: { data?: { message?: string | string[] } } }) => {
      const msg = err.response?.data?.message;
      message.error(Array.isArray(msg) ? msg.join(', ') : msg || '검증 리포트 조회 실패');
    },
  });

  const groupColumns = useMemo(
    () =>
      buildGroupMonitoringColumns({
        onOpenDetail: (g) => setDetailGroupId(g._id),
        serverSort: { field: groupSortField, order: groupSortOrder },
      }),
    [groupSortField, groupSortOrder],
  );

  const handleGroupsTableChange = (
    pagination: TablePaginationConfig,
    _filters: unknown,
    sorter: SorterResult<GroupMonitoringGroup> | SorterResult<GroupMonitoringGroup>[],
  ) => {
    const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const nextField = activeSorter?.field;
    const nextOrder = activeSorter?.order;
    let sortChanged = false;

    if (typeof nextField === 'string') {
      sortChanged = nextField !== groupSortField || nextOrder !== groupSortOrder;
      setGroupSortField(nextField as GroupMonitoringSortField);
      setGroupSortOrder(nextOrder);
    } else if (!nextOrder && (groupSortField || groupSortOrder)) {
      sortChanged = true;
      setGroupSortField(undefined);
      setGroupSortOrder(undefined);
    }

    if (sortChanged) {
      setPage(0);
      return;
    }

    if (pagination.current != null) {
      const nextPage = pagination.current - 1;
      const nextSize = pagination.pageSize ?? pageSize;
      if (nextSize !== pageSize) {
        setPageSize(nextSize);
        setPage(0);
        return;
      }
      setPage(nextPage);
    }
  };

  const applyListTableChange = (
    pagination: TablePaginationConfig,
    sorter: SorterResult<Record<string, unknown>> | SorterResult<Record<string, unknown>>[],
    currentPageSize: number,
    setCurrentPage: (page: number) => void,
    setCurrentPageSize: (size: number) => void,
  ) => {
    const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const nextField = activeSorter?.field;
    const nextOrder = activeSorter?.order;
    let sortChanged = false;

    if (typeof nextField === 'string') {
      sortChanged = nextField !== listSortField || nextOrder !== listSortOrder;
      setListSortField(nextField as GroupMonitoringSortField);
      setListSortOrder(nextOrder);
    } else if (!nextOrder && (listSortField || listSortOrder)) {
      sortChanged = true;
      setListSortField(undefined);
      setListSortOrder(undefined);
    }

    if (sortChanged) {
      setOptionsPage(0);
      setPartnersPage(0);
      setProductsPage(0);
      return;
    }

    if (pagination.current != null) {
      const nextPage = pagination.current - 1;
      const nextSize = pagination.pageSize ?? currentPageSize;
      if (nextSize !== currentPageSize) {
        setCurrentPageSize(nextSize);
        setCurrentPage(0);
        return;
      }
      setCurrentPage(nextPage);
    }
  };

  const auditIssues = useMemo(() => {
    const rows = auditData?.issues || [];
    return rows.filter((row) => {
      if (auditOnlyWideAgeGap && row.code !== 'V010_GroupWideAgeGap') return false;
      if (auditIssuesCodeFilter && row.code !== auditIssuesCodeFilter) return false;
      return true;
    });
  }, [auditData?.issues, auditOnlyWideAgeGap, auditIssuesCodeFilter]);
  const auditIssueCodes = useMemo(
    () => Array.from(new Set((auditData?.issues || []).map((row) => row.code))).sort(),
    [auditData?.issues],
  );
  const auditColumns: ColumnsType<GroupMonitoringAuditIssue> = [
    { title: 'Severity', dataIndex: 'severity', width: 86 },
    { title: 'Code', dataIndex: 'code', width: 168, ellipsis: true },
    {
      title: '상품',
      width: 160,
      ellipsis: true,
      render: (_, row) => row.context?.productName || '-',
    },
    {
      title: '주문',
      width: 140,
      ellipsis: true,
      render: (_, row) => row.context?.order?.orderUid || row.context?.order?.orderId || '-',
    },
    {
      title: '진단',
      width: 120,
      render: (_, row) => row.context?.diagnosisCategory || row.context?.exclusionReason || '-',
    },
    { title: 'Schedule ID', dataIndex: 'scheduleId', width: 120, ellipsis: true, render: (v) => v || '-' },
    { title: '메시지', dataIndex: 'message', ellipsis: true },
  ];

  const partnerColumns = useMemo<ColumnsType<GroupMonitoringPartnerRow>>(
    () => [
      withListServerSort(
        'partnerName',
        { title: '파트너', dataIndex: 'partnerName', width: 200, ellipsis: true },
        listServerSort,
      ),
      withListServerSort('productCount', { title: '상품', dataIndex: 'productCount', width: 72 }, listServerSort),
      withListServerSort('optionCount', { title: '회차', dataIndex: 'optionCount', width: 72 }, listServerSort),
      withListServerSort('enrollmentCount', { title: '배정', dataIndex: 'enrollmentCount', width: 72 }, listServerSort),
      withListServerSort('groupCount', { title: '그룹', dataIndex: 'groupCount', width: 72 }, listServerSort),
      shortGroupCountColumn<GroupMonitoringPartnerRow>(72, listServerSort),
      withListServerSort(
        'unassignedCount',
        { title: '배정 갭', dataIndex: 'unassignedCount', width: 88 },
        listServerSort,
      ),
      {
        title: '',
        width: 80,
        render: (_, r) => (
          <a
            onClick={(e) => {
              e.preventDefault();
              setPartnerId(r.partnerId);
            }}>
            상품 보기
          </a>
        ),
      },
    ],
    [listServerSort],
  );

  const productColumns = useMemo<ColumnsType<GroupMonitoringProductRow>>(
    () => [
      withListServerSort(
        'partnerName',
        { title: '파트너', dataIndex: 'partnerName', width: 160, ellipsis: true },
        listServerSort,
      ),
      withListServerSort(
        'productName',
        { title: '상품', dataIndex: 'productName', width: 220, ellipsis: true },
        listServerSort,
      ),
      withListServerSort('optionCount', { title: '회차', dataIndex: 'optionCount', width: 72 }, listServerSort),
      withListServerSort('enrollmentCount', { title: '배정', dataIndex: 'enrollmentCount', width: 72 }, listServerSort),
      withListServerSort('groupCount', { title: '그룹', dataIndex: 'groupCount', width: 72 }, listServerSort),
      shortGroupCountColumn<GroupMonitoringProductRow>(72, listServerSort),
      withListServerSort(
        'unassignedCount',
        { title: '배정 갭', dataIndex: 'unassignedCount', width: 88 },
        listServerSort,
      ),
      {
        title: '',
        width: 80,
        render: (_, r) => (
          <a
            onClick={(e) => {
              e.preventDefault();
              setProductId(r.productId);
            }}>
            회차 보기
          </a>
        ),
      },
    ],
    [listServerSort],
  );

  const unassignedColumns: ColumnsType<GroupMonitoringUnassignedEnrollment> = [
    { title: '파트너', dataIndex: 'partnerName', width: 120, ellipsis: true },
    { title: '상품', dataIndex: 'productName', width: 160, ellipsis: true },
    { title: '그룹핑 회차', dataIndex: 'optionName', width: 140, ellipsis: true },
    {
      title: '수업일',
      dataIndex: 'startDate',
      width: 130,
      render: (v) => dayjs(v).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: '회차 그룹 수',
      dataIndex: 'groupCountInOption',
      width: 88,
      align: 'center',
    },
    {
      title: '참가자',
      width: 100,
      ellipsis: true,
      render: (_, r) => (
        <Space size={2}>
          <span>{r.participantName || '-'}</span>
          {r.resolvedFromNotes && <Tag color="cyan">notes</Tag>}
        </Space>
      ),
    },
    { title: '구매자', width: 100, ellipsis: true, render: (_, r) => r.buyerName || '-' },
    { title: '플랫폼', dataIndex: 'sourceName', width: 72, render: (v) => v || '-' },
    { title: '주문번호', dataIndex: 'orderUid', width: 110, ellipsis: true, render: (v) => v || '-' },
    {
      title: '',
      width: 140,
      fixed: 'right',
      render: (_, r) => {
        const optKey = optionReprocessKey(r);
        const canReprocess = !!r.optionId;
        return (
          <Space size={0}>
            {canReprocess ? (
              <Popconfirm
                title="이 회차만 재배정"
                description={
                  <>
                    <div>{r.optionName}</div>
                    <div style={{ fontSize: 12, color: '#888' }}>
                      미배정 결제건을 기존 그룹에 반영합니다 (incremental).
                    </div>
                  </>
                }
                okText="재배정"
                cancelText="취소"
                onConfirm={() => {
                  setReprocessingOptionKey(optKey);
                  reprocessGapMutation.mutate({
                    productId: r.productId,
                    optionId: r.optionId!,
                    startDate: r.startDate,
                  });
                }}>
                <Button
                  type="link"
                  size="small"
                  loading={reprocessingOptionKey === optKey}
                  disabled={!!reprocessingOptionKey && reprocessingOptionKey !== optKey}>
                  회차 재배정
                </Button>
              </Popconfirm>
            ) : (
              <Button type="link" size="small" disabled title="optionId 없음 — 그룹배정 Run 후 재시도">
                회차 재배정
              </Button>
            )}
            <Button type="link" size="small" onClick={() => setSourceScheduleId(r.scheduleId)}>
              원본
            </Button>
          </Space>
        );
      },
    },
  ];

  const optionColumns = useMemo<ColumnsType<GroupMonitoringOptionRow>>(
    () => [
      withListServerSort(
        'partnerName',
        { title: '파트너', dataIndex: 'partnerName', ellipsis: true, width: 120 },
        listServerSort,
      ),
      withListServerSort(
        'productName',
        { title: '상품', dataIndex: 'productName', ellipsis: true, width: 160 },
        listServerSort,
      ),
      withListServerSort(
        'optionName',
        { title: '회차', dataIndex: 'optionName', width: 200, ellipsis: true },
        listServerSort,
      ),
      withListServerSort(
        'startDate',
        {
          title: '수업일',
          dataIndex: 'startDate',
          width: 140,
          render: (v) => dayjs(v).format('YYYY-MM-DD HH:mm'),
        },
        listServerSort,
      ),
      withListServerSort('enrollmentCount', { title: '배정', dataIndex: 'enrollmentCount', width: 64 }, listServerSort),
      withListServerSort(
        'scheduleCountInOption',
        {
          title: '회차전체',
          dataIndex: 'scheduleCountInOption',
          width: 72,
          render: (v?: number) => v ?? '-',
        },
        listServerSort,
      ),
      withListServerSort('groupCount', { title: '그룹', dataIndex: 'groupCount', width: 64 }, listServerSort),
      shortGroupCountColumn<GroupMonitoringOptionRow>(64, listServerSort),
      withListServerSort(
        'unassignedCount',
        { title: '배정 갭', dataIndex: 'unassignedCount', width: 88 },
        listServerSort,
      ),
      withListServerSort(
        'isStable',
        {
          title: '안정',
          dataIndex: 'isStable',
          width: 64,
          render: (v) => <Tag color={v ? 'green' : 'default'}>{v ? 'Y' : 'N'}</Tag>,
        },
        listServerSort,
      ),
      {
        title: '에이전트',
        width: 88,
        render: (_, r) =>
          r.latestAgentRunId ? <Link to={`/grouping-agent-runs/${r.latestAgentRunId}`}>Run</Link> : '-',
      },
    ],
    [listServerSort],
  );

  return (
    <div style={{ padding: 24 }}>
      <ReservationSuccessPageHeader
        title="그룹 현황"
        description="배정 갭은 결제됐지만 그룹에 들어가지 않은 건, 보충 필요는 최소 인원에 못 미친 그룹, 안정 회차는 배정 갭이 없는 회차입니다."
      />
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={
          dateType === 'past'
            ? '지난 수업일 기준으로 조회합니다. 최근 지난 회차가 위에 표시됩니다.'
            : '오늘 이후 예정된 수업일만 표시합니다. 가까운 수업일이 먼저 나옵니다.'
        }
      />

      <Space wrap style={{ marginBottom: 8 }} align="center">
        <Text type="secondary">수업일</Text>
        <Segmented
          value={dateType}
          options={dateTypeOptions}
          onChange={(v) => {
            setDateType(v as 'upcoming' | 'past');
            setPage(0);
            setUnassignedPage(0);
            setGroupSortField(undefined);
            setGroupSortOrder(undefined);
            setListSortField(undefined);
            setListSortOrder(undefined);
            setOptionsPage(0);
            setPartnersPage(0);
            setProductsPage(0);
          }}
        />
        <Input
          className={filterActiveClassName(!!keyword)}
          placeholder="파트너/상품/회차/그룹 검색"
          style={{ width: 240 }}
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          allowClear
        />
        <FilterSelect
          inactiveValue="all"
          style={{ width: 120 }}
          value={groupStatus}
          options={[
            { value: 'all', label: '그룹 상태 전체' },
            { value: 'complete', label: '완료' },
            { value: 'short', label: '보충 필요' },
          ]}
          onChange={(v) => {
            setGroupStatus(v);
            setPage(0);
            setOptionsPage(0);
            setPartnersPage(0);
            setProductsPage(0);
          }}
        />
        <Checkbox checked={kakaoOnly} onChange={(e) => setKakaoOnly(e.target.checked)}>
          카카오톡 예약하기 포함
        </Checkbox>
        <Checkbox
          checked={wideAgeGapOnly}
          onChange={(e) => {
            setWideAgeGapOnly(e.target.checked);
            setPage(0);
          }}>
          나이 편차 큰 그룹만
        </Checkbox>
        {partnerId && (
          <Tag closable onClose={() => setPartnerId(undefined)}>
            파트너 필터
          </Tag>
        )}
        {productId && (
          <Tag closable onClose={() => setProductId(undefined)}>
            상품 필터
          </Tag>
        )}
      </Space>
      <Space wrap style={{ marginBottom: 16 }} align="center">
        <Text type="secondary">데이터 정리</Text>
        <Button loading={pruneMutation.isPending} onClick={() => pruneMutation.mutate()}>
          환불/취소 참여자 제거
        </Button>
        <Popconfirm
          title="0명 그룹 삭제"
          description={
            <>
              <div>현재 필터(수업일·파트너·상품·검색·그룹 상태)에 해당하는 그룹 중</div>
              <div>참여자가 0명인 그룹을 삭제합니다. 되돌릴 수 없습니다.</div>
            </>
          }
          okText="삭제"
          cancelText="취소"
          okButtonProps={{ danger: true }}
          onConfirm={() => deleteEmptyGroupsMutation.mutate()}>
          <Button loading={deleteEmptyGroupsMutation.isPending}>0명 그룹 삭제</Button>
        </Popconfirm>
        <Button loading={recomputeAgeGapsMutation.isPending} onClick={() => recomputeAgeGapsMutation.mutate()}>
          나이 편차 재계산
        </Button>
        <Button loading={auditMutation.isPending} onClick={() => auditMutation.mutate()}>
          검증 리포트 실행 · RAW 다운로드
        </Button>
      </Space>

      {auditData && (
        <Card size="small" style={{ marginBottom: 16 }} title="검증 리포트 요약">
          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
            오류·경고·안내는 이슈 심각도입니다. PSV 동기화는 결제 정보와 그룹 배정이 어긋난 건, 고아 참조는 없는 그룹을
            가리키는 건입니다.
          </Text>
          <Space wrap style={{ marginBottom: 8 }}>
            <Tag color="red">오류 {auditData.summaryBySeverity.error || 0}</Tag>
            <Tag color="orange">경고 {auditData.summaryBySeverity.warn || 0}</Tag>
            <Tag color="blue">안내 {auditData.summaryBySeverity.info || 0}</Tag>
            <Tag>검증대상 회차 {auditData.stats.auditedOptionCount ?? '-'}</Tag>
            <Tag color="default">제외 회차 {auditData.stats.skippedOptionCount ?? '-'}</Tag>
            <Tag>완료결제 {auditData.stats.scheduleCount ?? '-'}</Tag>
            <Tag>배정률 {auditData.stats.assignmentRate}%</Tag>
            <Tag>배정갭 {auditData.stats.unassignedCount}</Tag>
            {auditData.stats.foreignMemberCount != null && (
              <Tag color="volcano">그룹 밖 결제 {auditData.stats.foreignMemberCount}</Tag>
            )}
            {auditData.stats.foreignByDiagnosis?.psv_sync_gap != null && (
              <Tag color="orange">PSV 동기화 {auditData.stats.foreignByDiagnosis.psv_sync_gap}</Tag>
            )}
            {auditData.stats.foreignByDiagnosis?.orphan_group_reference != null && (
              <Tag>고아 참조 {auditData.stats.foreignByDiagnosis.orphan_group_reference}</Tag>
            )}
            <Tag>안정회차 {auditData.stats.stableOptionCount}</Tag>
            {auditData.meta?.version && <Tag color="purple">{auditData.meta.version}</Tag>}
          </Space>
          <Space wrap style={{ marginBottom: 8 }}>
            <Checkbox checked={auditOnlyWideAgeGap} onChange={(e) => setAuditOnlyWideAgeGap(e.target.checked)}>
              나이 편차 큰 그룹만
            </Checkbox>
            <FilterSelect
              allowClear
              style={{ width: 260 }}
              placeholder="이슈 코드 필터"
              value={auditIssuesCodeFilter}
              options={auditIssueCodes.map((code) => ({ value: code, label: code }))}
              onChange={(v) => setAuditIssuesCodeFilter(v)}
            />
          </Space>
          <Table<GroupMonitoringAuditIssue>
            rowKey={(r) => `${r.code}-${r.optionKey}-${r.groupId || ''}-${r.scheduleId || ''}-${r.message}`}
            dataSource={auditIssues}
            columns={auditColumns}
            tableLayout="fixed"
            scroll={{ x: 1100 }}
            pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: [20, 50, 100] }}
          />
        </Card>
      )}

      <Card size="small" style={{ marginBottom: 24 }} title={summaryTitleByDateType[dateType]}>
        <Row gutter={[16, 16]}>
          <Col>
            <Statistic title="그룹핑 파트너" value={summary?.partnerCount ?? '-'} loading={summaryQuery.isLoading} />
          </Col>
          <Col>
            <Statistic title="그룹핑 상품" value={summary?.productCount ?? '-'} loading={summaryQuery.isLoading} />
          </Col>
          <Col>
            <Statistic title="그룹핑 회차" value={summary?.optionCount ?? '-'} loading={summaryQuery.isLoading} />
          </Col>
          <Col>
            <Statistic title="배정 결제건" value={summary?.enrollmentCount ?? '-'} loading={summaryQuery.isLoading} />
          </Col>
          <Col>
            <Statistic title="그룹 수" value={summary?.groupCount ?? '-'} loading={summaryQuery.isLoading} />
          </Col>
          <Col>
            <Statistic title="완료 그룹" value={summary?.completeGroupCount ?? '-'} loading={summaryQuery.isLoading} />
          </Col>
          <Col>
            <Statistic
              title="보충 필요 그룹"
              value={summaryQuery.isLoading ? undefined : formatShortGroupCountStat(summary?.shortGroupCount)}
              loading={summaryQuery.isLoading}
            />
          </Col>
          <Col>
            <Statistic title="안정 회차" value={summary?.stableOptionCount ?? '-'} loading={summaryQuery.isLoading} />
          </Col>
          <Col>
            <Statistic
              title={
                <Button
                  type="link"
                  size="small"
                  style={{ padding: 0, height: 'auto' }}
                  onClick={() => setActiveTab('unassigned')}>
                  배정 갭
                </Button>
              }
              value={summary?.unassignedCount ?? '-'}
              loading={summaryQuery.isLoading}
            />
          </Col>
        </Row>
      </Card>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'unassigned',
            label: `배정 갭 (${unassigned?.total ?? summary?.unassignedCount ?? 0})`,
            children: (
              <>
                <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
                  결제됐지만 어떤 그룹에도 들어가지 않은 건입니다. 같은 회차는 회차 재배정으로 증분 반영할 수 있습니다.
                  배정 뒤에 새로 결제된 건이 여기 남습니다.
                </Text>
                <Table<GroupMonitoringUnassignedEnrollment>
                  rowKey="scheduleId"
                  loading={unassignedQuery.isLoading}
                  dataSource={unassigned?.contents || []}
                  columns={unassignedColumns}
                  tableLayout="fixed"
                  scroll={{ x: 1100 }}
                  pagination={{
                    ...tablePaginationConfig(unassignedPageSize, (size) => {
                      setUnassignedPageSize(size);
                      setUnassignedPage(0);
                    }),
                    current: (unassigned?.page ?? 0) + 1,
                    total: unassigned?.total ?? 0,
                    onChange: (p, size) => {
                      if (size && size !== unassignedPageSize) {
                        setUnassignedPageSize(size);
                        setUnassignedPage(0);
                        return;
                      }
                      setUnassignedPage(p - 1);
                    },
                  }}
                />
              </>
            ),
          },
          {
            key: 'groups',
            label: `그룹 (${groups?.total ?? 0})`,
            children: (
              <Table<GroupMonitoringGroup>
                rowKey="_id"
                loading={groupsQuery.isLoading}
                dataSource={groups?.contents || []}
                columns={groupColumns}
                scroll={{ x: 1400 }}
                onChange={handleGroupsTableChange}
                pagination={{
                  ...tablePaginationConfig(pageSize, (size) => {
                    setPageSize(size);
                    setPage(0);
                  }),
                  current: (groups?.page ?? 0) + 1,
                  total: groups?.total ?? 0,
                }}
              />
            ),
          },
          {
            key: 'options',
            label: `그룹핑 회차 (${options?.total ?? 0})`,
            children: (
              <Table<GroupMonitoringOptionRow>
                rowKey={(r) => `${r.productId}-${r.optionName}-${r.startDate}`}
                loading={optionsQuery.isLoading}
                dataSource={options?.contents || []}
                columns={optionColumns}
                tableLayout="fixed"
                scroll={{ x: 1100 }}
                onChange={(pagination, _filters, sorter) =>
                  applyListTableChange(
                    pagination,
                    sorter as SorterResult<Record<string, unknown>> | SorterResult<Record<string, unknown>>[],
                    optionsPageSize,
                    setOptionsPage,
                    setOptionsPageSize,
                  )
                }
                pagination={{
                  ...tablePaginationConfig(optionsPageSize, (size) => {
                    setOptionsPageSize(size);
                    setOptionsPage(0);
                  }),
                  current: (options?.page ?? 0) + 1,
                  total: options?.total ?? 0,
                }}
              />
            ),
          },
          {
            key: 'partners',
            label: `그룹핑 파트너 (${partners?.total ?? 0})`,
            children: (
              <Table<GroupMonitoringPartnerRow>
                rowKey="partnerId"
                loading={partnersQuery.isLoading}
                dataSource={partners?.contents || []}
                columns={partnerColumns}
                tableLayout="fixed"
                scroll={{ x: 720 }}
                onChange={(pagination, _filters, sorter) =>
                  applyListTableChange(
                    pagination,
                    sorter as SorterResult<Record<string, unknown>> | SorterResult<Record<string, unknown>>[],
                    partnersPageSize,
                    setPartnersPage,
                    setPartnersPageSize,
                  )
                }
                pagination={{
                  ...tablePaginationConfig(partnersPageSize, (size) => {
                    setPartnersPageSize(size);
                    setPartnersPage(0);
                  }),
                  current: (partners?.page ?? 0) + 1,
                  total: partners?.total ?? 0,
                }}
              />
            ),
          },
          {
            key: 'products',
            label: `그룹핑 상품 (${products?.total ?? 0})`,
            children: (
              <Table<GroupMonitoringProductRow>
                rowKey={(r) => `${r.partnerId}-${r.productId}`}
                loading={productsQuery.isLoading}
                dataSource={products?.contents || []}
                columns={productColumns}
                tableLayout="fixed"
                scroll={{ x: 880 }}
                onChange={(pagination, _filters, sorter) =>
                  applyListTableChange(
                    pagination,
                    sorter as SorterResult<Record<string, unknown>> | SorterResult<Record<string, unknown>>[],
                    productsPageSize,
                    setProductsPage,
                    setProductsPageSize,
                  )
                }
                pagination={{
                  ...tablePaginationConfig(productsPageSize, (size) => {
                    setProductsPageSize(size);
                    setProductsPage(0);
                  }),
                  current: (products?.page ?? 0) + 1,
                  total: products?.total ?? 0,
                }}
              />
            ),
          },
        ]}
      />

      <GroupMonitoringDetailDrawer
        groupId={detailGroupId}
        open={!!detailGroupId}
        onClose={() => setDetailGroupId(null)}
      />
      <EnrollmentSourceDrawer
        scheduleId={sourceScheduleId}
        open={!!sourceScheduleId}
        onClose={() => setSourceScheduleId(null)}
        apiSource="group-monitoring"
      />
    </div>
  );
};

export default GroupMonitoringPage;
